/**
 * @fileoverview Canonical price-label copy + branch order for every web
 * surface that renders an event's ticket price (WEB-324).
 *
 * Before this module landed, three surfaces disagreed about what to show
 * when the upstream feed didn't supply a real price:
 *
 *   - `DiscoverEventCard`     — correctly gated on `priceKnown` and rendered
 *                               the muted **"Price not listed"** string.
 *   - `DiscoverMapHoverCard`  — ignored `priceKnown`; `isFree=false` fell
 *                               through to `Intl.NumberFormat.format(0)` →
 *                               a confidently-wrong **"$0"**.
 *   - Event-detail `AtAGlanceCard` — `priceToCents(undefined) → 0` rendered
 *                               **"From $0"**, with no `priceKnown` gate.
 *
 * A confidently-wrong "$0" is worse than an honest "Price not listed". This
 * module is now the **single source of truth** for:
 *
 *   1. The exact "Price not listed" string (`PRICE_NOT_LISTED`).
 *   2. The exact "FREE" string (`PRICE_FREE`).
 *   3. The canonical branch order (`formatEventPriceLabel`):
 *        priceKnown === false  → "Price not listed"
 *        isFree                → "FREE"
 *        priceMax > priceMin   → "$min–$max"
 *        else                  → "$min"
 *
 * Every web-side string literal naming an event price MUST flow through this
 * module — components import the constant or call `formatEventPriceLabel()`;
 * Storybook/test fixtures do the same. The branch order mirrors the original
 * `DiscoverEventCard` reference impl exactly so the refactor is
 * behavior-preserving.
 *
 * Pure data + pure function — no React imports — so the same copy ships
 * through both RSC and client renderers without a `'use client'` boundary.
 *
 * @since WEB-324
 */

/**
 * Canonical muted label rendered when the upstream feed did not supply a
 * real price (`priceKnown === false`). An honest absence beats a misleading
 * "$0". This is the exact string `DiscoverEventCard` rendered before the
 * WEB-324 extraction — do not drift it without a deliberate copy review.
 */
export const PRICE_NOT_LISTED = 'Price not listed' as const;

/** Canonical label for a known, zero-cost event. */
export const PRICE_FREE = 'FREE' as const;

/**
 * Normalized price inputs shared by every surface. Each call site maps its
 * own view-model shape (number `priceMin`/`priceMax` on `DiscoverEvent`, or
 * cents on the detail `AtAGlanceCard`) onto these three primitives.
 */
export interface EventPriceLabelInput {
  /**
   * Whether the upstream feed supplied a real price. When `false` the
   * `priceMin`/`priceMax` numbers are meaningless defaults (`0`) and the
   * label must be `PRICE_NOT_LISTED` regardless of their value.
   */
  readonly priceKnown: boolean;
  /**
   * `true` when the event is a known, zero-cost event. Callers derive this
   * from the canonical `priceMin === 0` predicate (NOT the convenience
   * `event.isFree` flag, which can drift during backend rollouts).
   */
  readonly isFree: boolean;
  /** Lowest published price in `currency` units (e.g. dollars). */
  readonly priceMin: number;
  /** Highest published price in `currency` units. Equals `priceMin` for single-tier events. */
  readonly priceMax: number;
  /** ISO 4217 currency code, e.g. `USD`. */
  readonly currency: string;
}

function priceHasFraction(amount: number): boolean {
  return Number.isFinite(amount) && !Number.isInteger(amount);
}

function formatPlainAmount(amount: number, fractional: boolean): string {
  return fractional ? amount.toFixed(2) : String(amount);
}

/**
 * Formats a known, non-free price as a single amount or a `$min–$max`
 * range. Whole-dollar amounts drop the cents (`$20`, `$18–$35`). A
 * fractional amount keeps two digits (`$8.31`, `$71.30`). Falls back to a
 * plain `$` prefix when the currency code is unknown to `Intl`.
 */
function formatKnownPrice(priceMin: number, priceMax: number, currency: string): string {
  const fractional = priceHasFraction(priceMin) || priceHasFraction(priceMax);
  const digits = fractional ? 2 : 0;
  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  } catch {
    // Unknown currency code — fall back to a plain dollar amount so the
    // surface never throws on a malformed feed value.
    const min = formatPlainAmount(priceMin, fractional);
    const max = formatPlainAmount(priceMax, fractional);
    return priceMax > priceMin ? `$${min}-$${max}` : `$${min}`;
  }
  if (priceMax > priceMin) {
    return `${formatter.format(priceMin)}-${formatter.format(priceMax)}`;
  }
  return formatter.format(priceMin);
}

/**
 * Single source of truth for the price label across the discover card, map
 * hover card, and event-detail at-a-glance card.
 *
 * Branch order is intentionally identical to the original
 * `DiscoverEventCard` reference impl:
 *
 *   1. `priceKnown === false` → `PRICE_NOT_LISTED` (honest absence)
 *   2. `isFree`               → `PRICE_FREE`
 *   3. `priceMax > priceMin`  → `"$min–$max"`
 *   4. else                   → `"$min"`
 */
export function formatEventPriceLabel(input: EventPriceLabelInput): string {
  if (!input.priceKnown) return PRICE_NOT_LISTED;
  if (input.isFree) return PRICE_FREE;
  return formatKnownPrice(input.priceMin, input.priceMax, input.currency);
}

/**
 * The `priceKnown`/`priceMin`/`priceMax`/`currency` quartet every discover
 * view-model carries — the minimal shape {@link formatKnownEventPriceLabel}
 * needs. `DiscoverEvent` satisfies this structurally; declared locally
 * (rather than importing `DiscoverEvent` from `@loci/types`) so this module
 * stays a plain data + pure-function file with no cross-package type coupling.
 */
export interface KnownEventPriceSource {
  readonly priceKnown: boolean;
  /**
   * API-1614 — nullable on the wire. The discover API emits `null` (rather than
   * the old fabricated `0`) whenever the upstream feed published no price, so
   * this shape must accept it. A `null` here means exactly what `priceKnown:
   * false` means; {@link formatKnownEventPriceLabel} treats both as "omit".
   */
  readonly priceMin: number | null;
  readonly priceMax: number | null;
  readonly currency: string;
}

/**
 * FRE-1197: the shared "quiet, optional price" gate — price is omitted
 * entirely (returns `null`, never rendered as `PRICE_NOT_LISTED`) when the
 * upstream feed didn't supply one. `isFree` derives from the canonical
 * `priceMin === 0` predicate, not a convenience flag. Single source of truth
 * for the `event.priceKnown ? formatPriceLabel(event) : null` gate every
 * price-optional surface (map takeover card, map hover card) needs — call
 * this instead of re-deriving the gate + `isFree` predicate locally.
 */
export function formatKnownEventPriceLabel(event: KnownEventPriceSource): string | null {
  if (!event.priceKnown) return null;
  // API-1614 — a null price is the honest "not published" signal and is omitted
  // exactly like `priceKnown: false`. Never coalesce it to `0`: that is the
  // fabrication this issue removed from the wire, and re-introducing it here
  // would render "Free" on an event whose price is simply unknown.
  if (event.priceMin === null) return null;
  const priceMax = event.priceMax ?? event.priceMin;
  return formatEventPriceLabel({
    priceKnown: true,
    isFree: event.priceMin === 0,
    priceMin: event.priceMin,
    priceMax,
    currency: event.currency,
  });
}

/**
 * Canonical predicate for "did the upstream feed supply a *real* price?"
 * when the only signal available is a free-form price string (the
 * event-detail `EventDetailV2.price` shape — there is no `priceKnown` field
 * on that API, unlike the discover view-model).
 *
 * A price is "known" only when the string parses to a finite number after
 * stripping currency symbols / grouping. Empty, `null`, `undefined`,
 * `"TBD"`, `"See site"` → `false` (→ `PRICE_NOT_LISTED`). This is
 * intentionally the exact parse `priceToCents` performs, so the
 * "is it known?" decision and the "what cents value?" decision can never
 * disagree — a divergence between those two was the original WEB-324
 * "From $0" bug.
 *
 * PIP-99: extracted here so `EventDetailTemplate` (and any future
 * string-price surface) delegates to one tested predicate instead of
 * re-deriving it inline, and so the price-honesty monitor
 * (`isFalseZeroPrice`) reuses the same definition.
 *
 * @since PIP-99 (consolidated from the WEB-324 inline `EventDetailTemplate.isPriceKnown`)
 */
export function isSourcePriceKnown(rawPrice: string | null | undefined): boolean {
  if (rawPrice === null || rawPrice === undefined) return false;
  if (rawPrice.trim().length === 0) return false;
  const numeric = Number.parseFloat(rawPrice.replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric);
}

/**
 * One currency amount: optional `$`, digits, optional thousands commas, and
 * an optional fractional part. Leftover words (`"From $20"`, `"See site"`)
 * do not match — stripping every non-digit would glue a range into one number.
 */
const SOURCE_AMOUNT = /^\$?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/;

/** A range separator: en dash, em dash, hyphen, or the word "to". */
const SOURCE_PRICE_RANGE = /^(.+?)\s*(?:–|—|-|\bto\b)\s*(.+)$/i;

function parseStrictSourceAmount(token: string): number | null {
  const match = SOURCE_AMOUNT.exec(token.trim());
  if (match == null) return null;
  const numeric = Number.parseFloat(`${match[1].replace(/,/g, '')}.${match[2] ?? '0'}`);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * A single amount, or a min/max pair. Anything else (a prefix, three bounds,
 * an unparsed token) is unsupported even when {@link isSourcePriceKnown}
 * is true — that predicate only asks whether any digit is present.
 */
function parseSourcePriceBounds(rawPrice: string): { min: number; max: number } | null {
  const range = SOURCE_PRICE_RANGE.exec(rawPrice.trim());
  if (range != null) {
    const min = parseStrictSourceAmount(range[1] ?? '');
    const max = parseStrictSourceAmount(range[2] ?? '');
    if (min == null || max == null) return null;
    return { min, max };
  }
  const amount = parseStrictSourceAmount(rawPrice);
  if (amount == null) return null;
  return { min: amount, max: amount };
}

/**
 * Format a free-form price string (`"8.31"`, `"71.3"`, `"$20"`,
 * `"$25 – $120"`) for a card. A range keeps both bounds. Unparseable input,
 * including words around a number, is {@link PRICE_NOT_LISTED}. A numeric
 * zero is FREE via {@link formatEventPriceLabel}.
 */
export function formatSourcePriceLabel(
  rawPrice: string | null | undefined,
  currency: string | null | undefined = 'USD',
): string {
  if (!isSourcePriceKnown(rawPrice) || rawPrice == null) return PRICE_NOT_LISTED;
  const bounds = parseSourcePriceBounds(rawPrice);
  if (bounds == null) return PRICE_NOT_LISTED;
  const code = currency != null && currency.trim() !== '' ? currency.trim() : 'USD';
  return formatEventPriceLabel({
    priceKnown: true,
    isFree: bounds.min === 0 && bounds.max === 0,
    priceMin: bounds.min,
    priceMax: bounds.max,
    currency: code,
  });
}
